import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { app, firebaseConfig, VAPID_KEY } from '@/lib/firebase';

type MessagingSetup = {
  messaging: any;
  registration: ServiceWorkerRegistration;
};

let messagingSetupCache: MessagingSetup | null = null;

async function getMessagingWithServiceWorker(): Promise<MessagingSetup | null> {
  if (messagingSetupCache) return messagingSetupCache;

  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  if (!firebaseConfig.apiKey) {
    console.warn('Firebase config not available');
    return null;
  }

  try {
    const messagingModule = await import('firebase/messaging');
    const isMessagingSupported = await messagingModule.isSupported();

    if (!isMessagingSupported) {
      console.info('Firebase Messaging no soportado en este navegador/contexto');
      return null;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });

    registration.active?.postMessage({
      type: 'FIREBASE_CONFIG',
      config: {
        apiKey: firebaseConfig.apiKey,
        projectId: firebaseConfig.projectId,
        messagingSenderId: firebaseConfig.messagingSenderId,
        appId: firebaseConfig.appId,
      },
    });

    const messaging = messagingModule.getMessaging(app);
    messagingSetupCache = { messaging, registration };
    return messagingSetupCache;
  } catch (error) {
    console.error('Error initializing Firebase Messaging:', error);
    return null;
  }
}

export function usePushNotifications() {
  const { user } = useAuth();
  const tokenSaved = useRef(false);
  const navigate = useNavigate();

  const requestPermissionAndSaveToken = useCallback(async () => {
    if (!user || tokenSaved.current) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator) || typeof window === 'undefined') return;

    try {
      const isEmbeddedPreview = window.self !== window.top;
      if (isEmbeddedPreview) {
        console.info('Push permission request skipped in embedded preview');
        return;
      }

      if (Notification.permission === 'denied') {
        console.info('Push notifications blocked by browser');
        return;
      }

      const permission =
        Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();

      if (permission !== 'granted') {
        console.log('Push notification permission denied or dismissed');
        return;
      }

      const setup = await getMessagingWithServiceWorker();
      if (!setup) return;

      if (!VAPID_KEY) {
        console.warn('VAPID key not configured');
        return;
      }

      const { getToken: getFCMToken, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) {
        console.info('Firebase Messaging no soportado para obtener token');
        return;
      }

      const token = await getFCMToken(setup.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: setup.registration,
      });

      if (!token) return;

      const { data: existingToken, error: selectError } = await supabase
        .from('push_tokens')
        .select('id')
        .eq('user_id', user.id)
        .eq('token', token)
        .maybeSingle();

      if (selectError) {
        console.error('Error checking push token:', selectError);
        return;
      }

      if (existingToken) {
        const { error: updateError } = await supabase
          .from('push_tokens')
          .update({
            device_info: navigator.userAgent,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingToken.id);

        if (updateError) {
          console.error('Error updating push token:', updateError);
          return;
        }
      } else {
        const { error: insertError } = await supabase.from('push_tokens').insert({
          user_id: user.id,
          token,
          device_info: navigator.userAgent,
        });

        if (insertError) {
          console.error('Error saving push token:', insertError);
          return;
        }
      }

      tokenSaved.current = true;
      console.log('Push token saved successfully');
    } catch (error) {
      console.error('Error setting up push notifications:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let unsubscribe: (() => void) | null = null;

    const setup = async () => {
      const isEmbeddedPreview = window.self !== window.top;
      if (isEmbeddedPreview) {
        console.info('onMessage initialization skipped in embedded preview');
        return;
      }

      const setupResult = await getMessagingWithServiceWorker();
      if (!setupResult?.messaging) return;

      try {
        const { onMessage, isSupported } = await import('firebase/messaging');
        if (!(await isSupported())) return;

        unsubscribe = onMessage(setupResult.messaging, (payload) => {
          const url = payload.data?.url || '/dashboard';
          toast(payload.notification?.title || 'Nueva notificación', {
            description: payload.notification?.body,
            action: {
              label: 'Ver',
              onClick: () => navigate(url),
            },
            duration: 8000,
          });
        });
      } catch (error) {
        console.error('No se pudo inicializar onMessage de Firebase:', error);
      }
    };

    setup();
    return () => unsubscribe?.();
  }, [user, navigate]);

  useEffect(() => {
    if (!user || tokenSaved.current || typeof window === 'undefined') return;

    const isEmbeddedPreview = window.self !== window.top;
    if (isEmbeddedPreview) return;

    // If permission is already granted, save token automatically.
    if (Notification.permission === 'granted') {
      const timer = setTimeout(() => requestPermissionAndSaveToken(), 500);
      return () => clearTimeout(timer);
    }

    // Safari/iOS commonly requires user gesture for Notification.requestPermission().
    if (Notification.permission === 'default') {
      const tryEnableOnGesture = () => {
        requestPermissionAndSaveToken();
      };

      window.addEventListener('click', tryEnableOnGesture, { once: true, passive: true });
      window.addEventListener('touchend', tryEnableOnGesture, { once: true, passive: true });
      window.addEventListener('keydown', tryEnableOnGesture, { once: true });

      toast.info('Activa notificaciones para recibir alertas con la pantalla bloqueada', {
        description: 'Toca cualquier parte de la app para autorizar notificaciones push.',
        duration: 6000,
      });

      return () => {
        window.removeEventListener('click', tryEnableOnGesture);
        window.removeEventListener('touchend', tryEnableOnGesture);
        window.removeEventListener('keydown', tryEnableOnGesture);
      };
    }
  }, [user, requestPermissionAndSaveToken]);

  return { requestPermissionAndSaveToken };
}
